import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { useSocket } from './SocketContext';

const PMContext = createContext();

export const usePMData = () => {
  const context = useContext(PMContext);
  if (!context) {
    throw new Error('usePMData must be used within a PMProvider');
  }
  return context;
};

export const PMProvider = ({ children }) => {
  const { user } = useAuth();
  const { connected, on, off } = useSocket();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard Data
  const [pmProjects, setPmProjects] = useState([]);
  const [pmTeamMembers, setPmTeamMembers] = useState([]);
  const [pmLeaveReqs, setPmLeaveReqs] = useState([]);
  const [pmDocs, setPmDocs] = useState([]);
  const [pmAnnouncements, setPmAnnouncements] = useState([]);
  const [pmHrReports, setPmHrReports] = useState([]);
  const [pmSchedules, setPmSchedules] = useState([]);
  const [pmInterns, setPmInterns] = useState([]);
  const [pmAdvances, setPmAdvances] = useState([]);
  const [assignedHR, setAssignedHR] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    pendingLeaves: 0,
    pendingDocs: 0,
    pendingReports: 0,
    pendingAdvances: 0,
    upcomingSchedules: 0,
    totalProjects: 0,
    totalMembers: 0
  });

  // =========================================
  // FETCH ALL PM DATA
  // =========================================
  const refreshPMData = useCallback(async () => {
    if (!user || user.role !== 'pm') return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Refreshing PM data...');
      
      const [
        dashboardRes,
        projectsRes,
        teamRes,
        leaveRes,
        docsRes,
        announceRes,
        reportsRes,
        schedulesRes,
        internsRes,
        advancesRes
      ] = await Promise.allSettled([
        api.getPMDashboard().catch(err => {
          console.warn('Dashboard fetch failed:', err.message);
          return { data: null };
        }),
        api.getPMProjects().catch(err => {
          console.warn('Projects fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMTeamMembers().catch(err => {
          console.warn('Team fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMLeaveRequests().catch(err => {
          console.warn('Leave fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMDocs().catch(err => {
          console.warn('Docs fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMAnnouncements().catch(err => {
          console.warn('Announcements fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMReports().catch(err => {
          console.warn('Reports fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMSchedules().catch(err => {
          console.warn('Schedules fetch failed:', err.message);
          return { data: [] };
        }),
        api.getPMInterns().catch(err => {
          console.warn('Interns fetch failed:', err.message);
          return { data: [] };
        }),
        api.getAdvances().catch(err => {
          console.warn('Advances fetch failed:', err.message);
          return { data: [] };
        })
      ]);

      if (dashboardRes.status === 'fulfilled' && dashboardRes.value) {
        const dashData = dashboardRes.value;
        setPmProjects(dashData.projects || []);
        setPmTeamMembers(dashData.teamMembers || []);
        setAssignedHR(dashData.assignedHR || []);
        setDashboardStats({
          pendingLeaves: dashData.pendingLeaves || 0,
          pendingDocs: dashData.pendingDocs || 0,
          pendingReports: dashData.pendingReports || 0,
          pendingAdvances: 0,
          upcomingSchedules: dashData.upcomingSchedules || 0,
          totalProjects: dashData.totalProjects || 0,
          totalMembers: dashData.totalMembers || 0
        });
      }

      if (projectsRes.status === 'fulfilled' && projectsRes.value) {
        setPmProjects(Array.isArray(projectsRes.value) ? projectsRes.value : []);
      }

      if (teamRes.status === 'fulfilled' && teamRes.value) {
        setPmTeamMembers(Array.isArray(teamRes.value) ? teamRes.value : []);
      }

      if (leaveRes.status === 'fulfilled' && leaveRes.value) {
        setPmLeaveReqs(Array.isArray(leaveRes.value) ? leaveRes.value : []);
      }

      if (docsRes.status === 'fulfilled' && docsRes.value) {
        setPmDocs(Array.isArray(docsRes.value) ? docsRes.value : []);
      }

      if (announceRes.status === 'fulfilled' && announceRes.value) {
        setPmAnnouncements(Array.isArray(announceRes.value) ? announceRes.value : []);
      }

      if (reportsRes.status === 'fulfilled' && reportsRes.value) {
        setPmHrReports(Array.isArray(reportsRes.value) ? reportsRes.value : []);
      }

      if (schedulesRes.status === 'fulfilled' && schedulesRes.value) {
        setPmSchedules(Array.isArray(schedulesRes.value) ? schedulesRes.value : []);
      }

      if (internsRes.status === 'fulfilled' && internsRes.value) {
        setPmInterns(Array.isArray(internsRes.value) ? internsRes.value : []);
      }

      if (advancesRes.status === 'fulfilled' && advancesRes.value) {
        const list = Array.isArray(advancesRes.value) ? advancesRes.value : [];
        setPmAdvances(list);
        setDashboardStats((prev) => ({ ...prev, pendingAdvances: list.length }));
      }

      console.log('✅ PM data refreshed successfully');
    } catch (err) {
      console.error('❌ Error refreshing PM data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'pm') {
      refreshPMData();
    }
  }, [user, refreshPMData]);

  // Realtime refresh for PM dashboard (HR reports, approvals, notifications)
  useEffect(() => {
    if (!user || user.role !== 'pm' || !connected) return;
    const handle = () => { refreshPMData().catch(() => {}); };
    on('notification', handle);
    on('notification:new', handle);
    on('leave:new', handle);
    on('leave:update', handle);
    on('permission:new', handle);
    on('permission:update', handle);
    on('payroll:update', handle);
    on('announcement:new', handle);
    on('calendar:event', handle);
    return () => {
      off('notification', handle);
      off('notification:new', handle);
      off('leave:new', handle);
      off('leave:update', handle);
      off('permission:new', handle);
      off('permission:update', handle);
      off('payroll:update', handle);
      off('announcement:new', handle);
      off('calendar:event', handle);
    };
  }, [user, connected, on, off, refreshPMData]);

  // =========================================
  // PROJECT MANAGEMENT
  // =========================================
  
  const createProject = async (projectData) => {
    try {
      setLoading(true);
      const response = await api.createPMProject(projectData);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Create project error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectId, projectData) => {
    try {
      setLoading(true);
      const response = await api.updatePMProject(projectId, projectData);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Update project error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      setLoading(true);
      const response = await api.deletePMProject(projectId);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Delete project error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getProjectById = async (projectId) => {
    try {
      const response = await api.getPMProjectById(projectId);
      return response;
    } catch (err) {
      console.error('❌ Get project error:', err);
      throw err;
    }
  };

  // =========================================
  // PROJECT MEMBERS
  // =========================================

  const getAvailableMembers = async (projectId) => {
    try {
      const response = await api.getPMAvailableMembers(projectId);
      return response;
    } catch (err) {
      console.error('❌ Get available members error:', err);
      throw err;
    }
  };

  const addProjectMember = async (projectId, userId, role) => {
    try {
      setLoading(true);
      const response = await api.addPMProjectMember(projectId, { userId, role });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Add member error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addProjectMembersBulk = async (projectId, members) => {
    // members = [{userId, role}, ...]
    try {
      setLoading(true);
      const response = await api.addPMProjectMembersBulk(projectId, members);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Bulk add members error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeProjectMember = async (projectId, userId) => {
    try {
      setLoading(true);
      const response = await api.removePMProjectMember(projectId, userId);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Remove member error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMemberProgress = async (projectId, userId, progress, status) => {
    try {
      setLoading(true);
      const response = await api.updatePMProjectMemberProgress(projectId, userId, { progress, status });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Update progress error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // REVIEWS
  // =========================================

  const addReview = async (userId, note, projectId) => {
    try {
      setLoading(true);
      const response = await api.addPMReview({ userId, note, projectId });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Add review error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getMemberReviews = async (userId) => {
    try {
      const response = await api.getPMMemberReviews(userId);
      return response;
    } catch (err) {
      console.error('❌ Get reviews error:', err);
      throw err;
    }
  };

  // =========================================
  // LEAVE MANAGEMENT
  // =========================================

  const handleLeaveAction = async (reqId, action, comment, days) => {
    try {
      setLoading(true);
      const response = await api.handlePMLeaveAction({ reqId, action, comment, days });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Leave action error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendLeave = async (reqId, days, comment) => {
    return handleLeaveAction(reqId, 'resend', comment, days);
  };

  // =========================================
  // DOCUMENT MANAGEMENT
  // =========================================

  const handleDocAction = async (docId, action, note) => {
    try {
      setLoading(true);
      const response = await api.handlePMDocAction({ docId, action, note });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Doc action error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getDocumentById = async (docId) => {
    try {
      const response = await api.getPMDocById(docId);
      return response;
    } catch (err) {
      console.error('❌ Get document error:', err);
      throw err;
    }
  };

  const getDocumentUrl = async (docId) => {
    try {
      const response = await api.getPMDocUrl(docId);
      return response;
    } catch (err) {
      console.error('❌ Get document URL error:', err);
      throw err;
    }
  };

  // =========================================
  // ANNOUNCEMENTS
  // =========================================

  const createAnnouncement = async (announcementData) => {
    try {
      setLoading(true);
      const response = await api.createPMAnnouncement(announcementData);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Create announcement error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // REPORTS
  // =========================================

  const handleReportAction = async (reportId, status, note) => {
    try {
      setLoading(true);
      const response = await api.handlePMReportAction({ reportId, status, note });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Report action error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyPayroll = async (reportId, status, comment) => {
    try {
      setLoading(true);
      const response = await api.verifyPMPayroll({ reportId, status, comment });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Verify payroll error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPayrollReport = async (reportId) => {
    try {
      const response = await api.getPMPayrollReport(reportId);
      return response;
    } catch (err) {
      console.error('❌ Get payroll error:', err);
      throw err;
    }
  };

  // =========================================
  // SCHEDULES
  // =========================================

  const createSchedule = async (scheduleData) => {
    try {
      setLoading(true);
      const response = await api.createPMSchedule(scheduleData);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Create schedule error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSchedule = async (scheduleId, status) => {
    try {
      setLoading(true);
      const response = await api.updatePMSchedule(scheduleId, { status });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Update schedule error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // INTERNS
  // =========================================

  const assignInternToProject = async (internId, projectId, role) => {
    try {
      setLoading(true);
      const response = await api.assignPMIntern({ internId, projectId, role });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Assign intern error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // TASKS
  // =========================================

  const createTask = async (taskData) => {
    try {
      setLoading(true);
      const response = await api.createPMTask(taskData);
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Create task error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      setLoading(true);
      const response = await api.updatePMTaskStatus(taskId, { status });
      await refreshPMData();
      return response;
    } catch (err) {
      console.error('❌ Update task error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // State
    loading,
    error,
    pmProjects,
    pmTeamMembers,
    pmLeaveReqs,
    pmDocs,
    pmAnnouncements,
    pmHrReports,
    pmSchedules,
    pmInterns,
    pmAdvances,
    assignedHR,
    dashboardStats,

    // Actions
    refreshPMData,
    
    // Projects
    createProject,
    updateProject,
    deleteProject,
    getProjectById,
    
    // Members
    getAvailableMembers,
    addProjectMember,
    addProjectMembersBulk,
    removeProjectMember,
    updateMemberProgress,
    
    // Reviews
    addReview,
    getMemberReviews,
    
    // Leave
    handleLeaveAction,
    resendLeave,
    
    // Documents
    handleDocAction,
    getDocumentById,
    getDocumentUrl,
    
    // Announcements
    createAnnouncement,
    
    // Reports
    handleReportAction,
    verifyPayroll,
    getPayrollReport,
    
    // Schedules
    createSchedule,
    updateSchedule,
    
    // Interns
    assignInternToProject,
    
    // Tasks
    createTask,
    updateTaskStatus
  };

  return (
    <PMContext.Provider value={value}>
      {children}
    </PMContext.Provider>
  );
};
